import { CUSTOMER_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosCustomerInstance from '../api/AxiosCustomerInstance';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { fetchRestaurantById } from './restaurantApi';
import { ensureMenuImageFile } from './menuImageUtils';
import { buildNutritionForSubmit, caloriesFromNutrition, resolveNutritionMap } from './foodNutrition';
import { mapIngredientOption, pickValue } from './restaurantApi';

export function resolveIngredientIdsFromMenuItem(item) {
    const fromIds = resolveIngredientIds(item);
    if (fromIds.length > 0) return fromIds;
    if (!Array.isArray(item?.ingredients)) return [];
    return item.ingredients
        .map((ing) =>
            Number(
                pickValue(ing, 'ingredientId', 'IngredientId', 'ingredient_id', 'Ingredient_Id', 'id')
            )
        )
        .filter((id) => Number.isFinite(id));
}

const DIET_TYPE_VALUES = new Set(['VEG', 'NONVEG', 'NON-VEG', 'NON_VEG', 'EGG']);

function isDietTypeValue(val) {
    if (val == null || String(val).trim() === '') return false;
    const normalized = String(val).trim().toUpperCase().replace(/_/g, '-');
    return DIET_TYPE_VALUES.has(normalized) || normalized === 'NON VEG';
}

function parseChannelFlag(val, defaultValue = true) {
    if (val == null || val === '') return defaultValue;
    if (typeof val === 'boolean') return val;
    const normalized = String(val).trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    return defaultValue;
}

/** Read sell-on flag from item; uses default only when the property is absent (not when false/0). */
export function resolveChannelFlag(item, keys, defaultValue = true) {
    if (!item || typeof item !== 'object') return defaultValue;
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
            return parseChannelFlag(item[key], defaultValue);
        }
    }
    return defaultValue;
}

export function normalizeDietType(val) {
    if (val == null || String(val).trim() === '') return '';
    const normalized = String(val).trim().toUpperCase().replace(/_/g, '-');
    if (normalized === 'NONVEG' || normalized === 'NON VEG') return 'NON-VEG';
    if (normalized === 'VEG' || normalized === 'NON-VEG' || normalized === 'EGG') return normalized;
    return String(val).trim();
}

/** User-facing label for VEG / NON-VEG (canonical values stay API-safe). */
export function formatDietTypeLabel(val) {
    const normalized = normalizeDietType(val);
    if (!normalized) return '';
    if (normalized === 'VEG') return 'Veg';
    if (normalized === 'NON-VEG') return 'Non-Veg';
    if (normalized === 'EGG') return 'Egg';
    return normalized;
}

export function formatMenuItemStatusLabel(val) {
    const normalized = String(val ?? 'ACTIVE').trim().toUpperCase();
    if (normalized === 'INACTIVE') return 'Inactive';
    if (normalized === 'ACTIVE') return 'Active';
    return val != null && String(val).trim() ? String(val).trim() : 'Active';
}

export function isMenuItemActiveStatus(val) {
    return String(val ?? 'ACTIVE').trim().toUpperCase() !== 'INACTIVE';
}

export const GROUP_SERVING_UNITS = [
    { value: 'KG', label: 'kg' },
    { value: 'PIECE', label: 'pcs' },
    { value: 'PORTION', label: 'portions' },
    { value: 'CUP', label: 'cups' },
    { value: 'ML', label: 'ml' },
];

export const GROUP_COURSE_ROLES = [
    { value: 'STARTER', label: 'Starters' },
    { value: 'MAIN', label: 'Mains' },
    { value: 'SIDE', label: 'Sides' },
    { value: 'DESSERT', label: 'Desserts' },
    { value: 'BAKERY', label: 'Bakery' },
    { value: 'DRINK', label: 'Drinks' },
];

export const EMPTY_GROUP_SCALE_FIELDS = {
    groupScaleEnabled: false,
    servingUnit: '',
    basePortionQty: '',
    peoplePerPortion: '',
    qtyStep: '',
    minOrderQty: '',
    courseRole: '',
    partyUnitPrice: '',
};

function parsePositiveNumber(raw) {
    if (raw == null || String(raw).trim() === '') return '';
    const n = Number(raw);
    return Number.isFinite(n) ? String(n) : '';
}

function parseNonNegativeNumber(raw) {
    if (raw == null || String(raw).trim() === '') return '';
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? String(n) : '';
}

export function mapGroupScaleFromApi(raw = {}) {
    return {
        groupScaleEnabled: parseChannelFlag(
            pickValue(raw, 'groupScaleEnabled', 'group_scale_enabled'),
            false
        ),
        servingUnit: String(pickValue(raw, 'servingUnit', 'serving_unit') ?? '').trim().toUpperCase(),
        basePortionQty: parsePositiveNumber(pickValue(raw, 'basePortionQty', 'base_portion_qty')),
        peoplePerPortion: parsePositiveNumber(
            pickValue(raw, 'peoplePerPortion', 'people_per_portion')
        ),
        qtyStep: parsePositiveNumber(pickValue(raw, 'qtyStep', 'qty_step')),
        minOrderQty: parsePositiveNumber(pickValue(raw, 'minOrderQty', 'min_order_qty')),
        courseRole: String(pickValue(raw, 'courseRole', 'course_role') ?? '').trim().toUpperCase(),
        partyUnitPrice: parseNonNegativeNumber(
            pickValue(raw, 'partyUnitPrice', 'party_unit_price')
        ),
    };
}

export function mapGroupScaleForSubmit(item = {}) {
    const enabled = Boolean(item.groupScaleEnabled);
    const payload = { groupScaleEnabled: enabled };
    if (!enabled) {
        return payload;
    }
    payload.servingUnit = String(item.servingUnit || '').trim().toUpperCase() || null;
    payload.basePortionQty =
        item.basePortionQty === '' || item.basePortionQty == null
            ? null
            : Number(item.basePortionQty);
    payload.peoplePerPortion =
        item.peoplePerPortion === '' || item.peoplePerPortion == null
            ? null
            : Number(item.peoplePerPortion);
    payload.qtyStep =
        item.qtyStep === '' || item.qtyStep == null ? null : Number(item.qtyStep);
    payload.minOrderQty =
        item.minOrderQty === '' || item.minOrderQty == null ? null : Number(item.minOrderQty);
    payload.courseRole = String(item.courseRole || '').trim().toUpperCase() || null;
    payload.partyUnitPrice =
        item.partyUnitPrice === '' || item.partyUnitPrice == null
            ? null
            : Number(item.partyUnitPrice);
    return payload;
}

export function resolveFoodItemImageFields(raw) {
    if (!raw || typeof raw !== 'object') return { imagePath: null, imageUrl: null };
    const imagePathVal = pickValue(raw, 'imagePath', 'Image_Path', 'image_path');
    const imagePath =
        imagePathVal != null && String(imagePathVal).trim() ? String(imagePathVal).trim() : null;
    const imageUrlVal = pickValue(raw, 'imageUrl', 'Image_Url', 'image_url');
    const imageUrl =
        imageUrlVal != null && String(imageUrlVal).trim() ? String(imageUrlVal).trim() : null;
    return { imagePath, imageUrl };
}

/**
 * GET /restaurants/:restaurantId/food-items — normalize each item for menu UI.
 */
export function normalizeOnlineFoodItem(raw) {
    if (!raw || typeof raw !== 'object') return null;

    const ingredientsRaw = raw.ingredients;
    let ingredients = [];
    if (Array.isArray(ingredientsRaw)) {
        ingredients = ingredientsRaw.map(mapIngredientOption).filter(Boolean);
    }

    const { imagePath, imageUrl } = resolveFoodItemImageFields(raw);

    const foodItemId =
        pickValue(raw, 'Food_Item_Id', 'food_item_id', 'foodItemId', 'FoodItemId') ?? null;

    const foodCategory =
        pickValue(raw, 'foodCategory', 'Food_Category', 'food_category', 'menuCategory') ?? '';

    let itemFoodType = normalizeDietType(
        pickValue(raw, 'itemFoodType', 'Item_Food_Type', 'food_type', 'foodType')
    );
    if (!itemFoodType) {
        const legacyType = pickValue(raw, 'Food_Type', 'foodType');
        if (isDietTypeValue(legacyType)) {
            itemFoodType = normalizeDietType(legacyType);
        }
    }

    const legacyCategory = pickValue(raw, 'Food_Type', 'foodType');
    const categoryForGroup =
        foodCategory ||
        (legacyCategory && !isDietTypeValue(legacyCategory) ? legacyCategory : '') ||
        'Other';

    return {
        Calories: caloriesFromNutrition(raw),
        Nutrition: resolveNutritionMap(raw),
        nutrition: resolveNutritionMap(raw),
        Food_Item: pickValue(raw, 'Food_Item', 'food_item', 'foodItem', 'foodItemName') ?? '',
        Food_Item_Id: foodItemId,
        Food_Type: categoryForGroup,
        foodCategory: foodCategory || categoryForGroup,
        itemFoodType,
        cuisineOrigin: pickValue(raw, 'cuisineOrigin', 'Cuisine_Origin', 'cuisine_origin') ?? '',
        Description: pickValue(raw, 'description', 'Description') ?? '',
        Currency: pickValue(raw, 'currency', 'Currency') ?? 'INR',
        Menu_Source: pickValue(raw, 'Menu_Source', 'menu_source', 'menuSource') ?? null,
        availableForDineIn: resolveChannelFlag(
            raw,
            ['availableForDineIn', 'available_for_dine_in', 'Available_For_Dine_In'],
            true
        ),
        availableForOnline: resolveChannelFlag(
            raw,
            ['availableForOnline', 'available_for_online', 'Available_For_Online'],
            true
        ),
        Status: pickValue(raw, 'Status', 'status') ?? 'ACTIVE',
        Notes: pickValue(raw, 'Notes', 'notes') ?? null,
        Price: pickValue(raw, 'Price', 'price') ?? '',
        Quantity: pickValue(raw, 'Quantity', 'quantity') ?? '',
        Master_Food_Item_Id:
            pickValue(raw, 'Master_Food_Item_Id', 'master_food_item_id', 'masterFoodItemId') ?? null,
        Inventory_Item_Id: pickValue(raw, 'Inventory_Item_Id', 'inventoryItemId', 'inventory_item_id') ?? null,
        imagePath,
        ...(imageUrl ? { imageUrl } : {}),
        ingredients,
        ingredientIds: ingredients
            .map((ing) => ing.ingredientId)
            .filter((id) => id != null && id !== ''),
        agePricing: Array.isArray(raw.agePricing ?? raw.age_pricing)
            ? (raw.agePricing ?? raw.age_pricing).map((tier) => ({
                  agePricingId: tier.agePricingId ?? tier.Age_Pricing_Id ?? null,
                  minAge: tier.minAge ?? tier.Min_Age ?? '',
                  maxAge: tier.maxAge ?? tier.Max_Age ?? '',
                  quantity: tier.quantity ?? tier.Quantity ?? '',
                  price: tier.price ?? tier.Price ?? '',
                  status: tier.status ?? tier.Status ?? 'ACTIVE',
              }))
            : [],
        ...mapGroupScaleFromApi(raw),
    };
}

/** Shared fields for POST/PUT /restaurant/food-items */
export function resolveIngredientIds(item) {
    const raw = item?.ingredientIds ?? item?.Ingredient_Ids ?? item?.ingredient_ids;
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => Number(id)).filter((id) => Number.isFinite(id));
}

export function mapOnlineFoodItemRequestFields(item, restaurantId, foodCategories = []) {
    let foodCategory =
        item.foodCategory ??
        item.Food_Category ??
        '';
    if (!foodCategory && item.foodCategoryId != null && String(item.foodCategoryId).trim() !== '') {
        const match = (Array.isArray(foodCategories) ? foodCategories : []).find(
            (cat) =>
                String(cat.food_category_id ?? cat.foodCategoryId ?? cat.id) ===
                String(item.foodCategoryId)
        );
        foodCategory =
            match?.food_category_name ?? match?.foodCategoryName ?? match?.Food_Type ?? '';
    }
    if (!foodCategory) {
        const legacyType = item.foodType ?? item.Food_Type ?? '';
        if (legacyType && !isDietTypeValue(legacyType)) {
            foodCategory = legacyType;
        }
    }
    const dietaryType = normalizeDietType(
        item.itemFoodType ??
            item.Item_Food_Type ??
            (['VEG', 'NONVEG', 'NON_VEG', 'NON-VEG', 'EGG'].includes(
                String(item.foodType ?? '').toUpperCase().replace(/_/g, '-')
            ) &&
            String(item.foodType) !== String(foodCategory)
                ? item.foodType
                : null)
    );

    const menuSourceRaw =
        item.menuSource ?? item.Menu_Source ?? item.menu_source ?? 'MYEATS';
    const menuSource = String(menuSourceRaw).trim().toUpperCase();
    const normalizedMenuSource = menuSource === 'JREATS' ? 'JREATS' : 'MYEATS';
    const defaultDineIn = normalizedMenuSource !== 'JREATS';
    const defaultOnline = true;
    const statusRaw = item.status ?? item.Status;
    const status =
        statusRaw != null && String(statusRaw).trim() !== ''
            ? String(statusRaw).trim().toUpperCase()
            : '';
    const ingredientIds = resolveIngredientIds(item);

    const categoryIdRaw = item.foodCategoryId ?? item.Food_Category_Id ?? item.food_category_id;
    const nutritionRows = item.nutrition ?? item.Nutrition ?? [];

    return {
        restaurantId: Number(restaurantId),
        foodCategory: String(foodCategory).trim(),
        ...(categoryIdRaw != null && String(categoryIdRaw).trim() !== ''
            ? { foodCategoryId: Number(categoryIdRaw) }
            : {}),
        foodType: dietaryType || 'VEG',
        foodItem: String(item.foodItem ?? item.Food_Item ?? '').trim(),
        price: String(item.price ?? item.Price ?? '').trim(),
        quantity: String(item.quantity ?? item.Quantity ?? '').trim(),
        cuisineOrigin: String(item.cuisineOrigin ?? item.Cuisine_Origin ?? '').trim(),
        description: String(
            item.description ?? item.Description ?? item.notes ?? item.Notes ?? ''
        ).trim(),
        currency: item.currency ?? item.Currency ?? 'INR',
        nutrition: buildNutritionForSubmit(nutritionRows),
        menuSource: normalizedMenuSource,
        availableForDineIn: resolveChannelFlag(
            item,
            ['availableForDineIn', 'available_for_dine_in', 'Available_For_Dine_In'],
            defaultDineIn
        ),
        availableForOnline: resolveChannelFlag(
            item,
            ['availableForOnline', 'available_for_online', 'Available_For_Online'],
            defaultOnline
        ),
        ...(status === 'ACTIVE' || status === 'INACTIVE' ? { status } : {}),
        ingredientIds,
        ...(Array.isArray(item.agePricing) && item.agePricing.length > 0
            ? { agePricing: normalizeAgePricingForSubmit(item.agePricing) }
            : Array.isArray(item.agePricing) && item.agePricing.length === 0
              ? { agePricing: [] }
              : {}),
        ...mapGroupScaleForSubmit(item),
    };
}

/** Strip empty rows and coerce types for API agePricing payload. */
export function normalizeAgePricingForSubmit(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .map((row) => ({
            minAge: row?.minAge ?? row?.Min_Age,
            maxAge: row?.maxAge ?? row?.Max_Age,
            quantity: row?.quantity ?? row?.Quantity,
            price: row?.price ?? row?.Price,
            status: row?.status ?? row?.Status ?? 'ACTIVE',
        }))
        .filter(
            (row) =>
                row.minAge !== '' &&
                row.minAge != null &&
                row.maxAge !== '' &&
                row.maxAge != null &&
                String(row.quantity ?? '').trim() &&
                row.price !== '' &&
                row.price != null
        )
        .map((row) => ({
            minAge: Number.parseInt(row.minAge, 10),
            maxAge: Number.parseInt(row.maxAge, 10),
            quantity: String(row.quantity).trim(),
            price: Number.parseFloat(row.price),
            status: String(row.status).toUpperCase() === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
        }))
        .filter(
            (row) =>
                Number.isFinite(row.minAge) &&
                Number.isFinite(row.maxAge) &&
                Number.isFinite(row.price)
        );
}

/** POST /restaurant/food-items */
export function buildOnlineFoodItemCreateBody(
    restaurantId,
    newMenuItem,
    createdById,
    foodCategories = []
) {
    return {
        ...mapOnlineFoodItemRequestFields(newMenuItem, restaurantId, foodCategories),
        createdById: Number(createdById),
    };
}

/** PUT /restaurant/food-items/:id */
export function buildOnlineFoodItemUpdateBody(
    restaurantId,
    item,
    updatedById,
    foodCategories = []
) {
    const ingredientIds = resolveIngredientIds(item);
    const body = {
        ...mapOnlineFoodItemRequestFields(item, restaurantId, foodCategories),
        ingredientIds,
        updatedById: Number(updatedById),
    };
    const masterId =
        item?.Master_Food_Item_Id ?? item?.masterFoodItemId ?? item?.master_food_item_id;
    if (masterId) {
        // Linked-to-master: only restaurant-owned fields may change.
        const allowed = {
            restaurantId: body.restaurantId,
            price: body.price,
            quantity: body.quantity,
            notes: body.notes,
            status: body.status,
            menuSource: body.menuSource,
            availableForDineIn: body.availableForDineIn,
            availableForOnline: body.availableForOnline,
            currency: body.currency,
            updatedById: body.updatedById,
            groupScaleEnabled: body.groupScaleEnabled,
            servingUnit: body.servingUnit,
            basePortionQty: body.basePortionQty,
            peoplePerPortion: body.peoplePerPortion,
            qtyStep: body.qtyStep,
            minOrderQty: body.minOrderQty,
            partyUnitPrice: body.partyUnitPrice,
            agePricing: body.agePricing,
        };
        return Object.fromEntries(
            Object.entries(allowed).filter(([, v]) => v !== undefined)
        );
    }
    return body;
}

async function postOrPutOnlineFoodItem(method, url, item, body) {
    const token = localStorage.getItem('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    if (item?.imageFile) {
        const imageFile = await ensureMenuImageFile(item.imageFile);
        const formData = new FormData();
        Object.entries(body).forEach(([key, value]) => {
            if (value == null || value === '') return;
            if (key === 'availableForDineIn' || key === 'availableForOnline') {
                formData.append(key, resolveChannelFlag({ [key]: value }, [key], true) ? '1' : '0');
                return;
            }
            if (typeof value === 'boolean') {
                formData.append(key, value ? '1' : '0');
                return;
            }
            if (key === 'agePricing' && Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
                return;
            }
            if (key === 'nutrition' && value && typeof value === 'object') {
                formData.append(key, JSON.stringify(value));
                return;
            }
            if (Array.isArray(value)) {
                if (value.length === 0 && key === 'ingredientIds') {
                    formData.append(key, '[]');
                    return;
                }
                value.forEach((entry) => {
                    if (entry != null && entry !== '') formData.append(key, String(entry));
                });
                return;
            }
            formData.append(key, String(value));
        });
        formData.append('image', imageFile, imageFile.name ?? 'menu-image.webp');
        return axiosRestaurantInstance.request({
            method,
            url,
            data: formData,
            headers: { ...headers, 'Content-Type': false },
        });
    }

    return axiosRestaurantInstance.request({ method, url, data: body, headers });
}

export async function createOnlineFoodItem(
    restaurantId,
    newMenuItem,
    createdById,
    options = {}
) {
    const foodCategories = options.foodCategories ?? [];
    const body = buildOnlineFoodItemCreateBody(
        restaurantId,
        newMenuItem,
        createdById,
        foodCategories
    );
    return postOrPutOnlineFoodItem(
        'post',
        RESTAURANT_API_PATHS.restaurantsFoodItems,
        newMenuItem,
        body
    );
}

export async function updateOnlineFoodItem(
    foodItemId,
    restaurantId,
    item,
    updatedById,
    options = {}
) {
    const foodCategories = options.foodCategories ?? [];
    const body = buildOnlineFoodItemUpdateBody(
        restaurantId,
        item,
        updatedById,
        foodCategories
    );
    return postOrPutOnlineFoodItem(
        'put',
        `${RESTAURANT_API_PATHS.restaurantsFoodItems}/${foodItemId}`,
        item,
        body
    );
}

/** PUT /restaurant/food-items/:id/age-pricing */
export async function replaceFoodItemAgePricing(foodItemId, agePricingRows, updatedById) {
    const token = localStorage.getItem('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.restaurantFoodItemAgePricingById(foodItemId),
        {
            agePricing: normalizeAgePricingForSubmit(agePricingRows),
            updatedById: Number(updatedById),
        },
        { headers }
    );
}

/** Grouped object { foodCategory: [ normalized items ] } from food-items JSON body */
export function mapFoodItemsApiResponse(data) {
    if (!data || typeof data !== 'object') return {};
    const out = {};

    if (Array.isArray(data.items)) {
        data.items.forEach((raw) => {
            const item = normalizeOnlineFoodItem(raw);
            if (!item) return;
            const cat = item.foodCategory || item.Food_Type || 'Other';
            if (!out[cat]) out[cat] = [];
            out[cat].push(item);
        });
        return out;
    }

    Object.keys(data).forEach((foodType) => {
        const list = Array.isArray(data[foodType]) ? data[foodType] : [];
        const normalized = list.map(normalizeOnlineFoodItem).filter(Boolean);
        if (normalized.length > 0) out[foodType] = normalized;
    });
    return out;
}

export function normalizeMenuListSource(val) {
    const source = String(val ?? 'MYEATS').trim().toUpperCase().replace(/-/g, '');
    if (source === 'JREATS') return 'JREATS';
    if (source === 'DINEIN') return 'DINEIN';
    return 'MYEATS';
}

/**
 * Food catalog query for template / daily-menu pickers.
 * DINEIN → MYEATS catalog + dine-in flag; MYEATS → MYEATS catalog + online flag.
 */
export function getCatalogOptionsForTemplateSource(templateSource) {
    const source = normalizeMenuListSource(templateSource);
    if (source === 'JREATS') {
        return { menuSource: 'JREATS' };
    }
    if (source === 'DINEIN') {
        return { menuSource: 'MYEATS', salesChannel: 'dinein' };
    }
    return { menuSource: 'MYEATS', salesChannel: 'online' };
}

/** Client-side guard: MYEATS catalog row matches template source channel flags. */
export function foodItemMatchesTemplateSourceCatalog(item, templateSource) {
    const source = normalizeMenuListSource(templateSource);
    if (source === 'JREATS') return true;
    const menuSrc = String(
        item?.Menu_Source ?? item?.menuSource ?? item?.menu_source ?? 'MYEATS'
    )
        .trim()
        .toUpperCase()
        .replace(/-/g, '');
    if (menuSrc !== 'MYEATS') return false;
    if (source === 'DINEIN') {
        return resolveChannelFlag(
            item,
            ['availableForDineIn', 'available_for_dine_in', 'Available_For_Dine_In'],
            true
        );
    }
    return resolveChannelFlag(
        item,
        ['availableForOnline', 'available_for_online', 'Available_For_Online'],
        true
    );
}

/** Keep only catalog rows for MYEATS, JREATS, or DINEIN (catalog rows remain MYEATS in DB). */
export function filterGroupedMenuItemsBySource(grouped, source) {
    const wanted = normalizeMenuListSource(source);
    const catalogSource = wanted === 'DINEIN' ? 'MYEATS' : wanted;
    const out = {};
    Object.entries(grouped || {}).forEach(([categoryKey, list]) => {
        const arr = (Array.isArray(list) ? list : []).filter((item) => {
            const itemSrc = String(
                item.Menu_Source ?? item.menuSource ?? item.menu_source ?? 'MYEATS'
            )
                .trim()
                .toUpperCase();
            return itemSrc === catalogSource;
        });
        if (arr.length > 0) {
            out[categoryKey] = arr;
        }
    });
    return out;
}

/**
 * Load online menu catalog grouped by category for the active list source.
 * MYEATS / DINEIN: restaurant food-items API. JREATS: customer full-menu API.
 */
export async function loadGroupedOnlineMenuItems(restaurantId, source = 'MYEATS', options = {}) {
    const normalized = normalizeMenuListSource(source);
    const salesChannel = normalizeSalesChannel(
        options?.salesChannel ?? options?.sales_channel
    );
    if (normalized === 'JREATS') {
        const restaurant = await fetchRestaurantById(restaurantId);
        const restaurantState = restaurantAddressState(restaurant);
        const grouped = await fetchRestaurantFoodItems(restaurantId, {
            menuSource: 'JREATS',
            salesChannel,
        });
        return {
            items: grouped,
            meta: {
                restaurantState,
                state: restaurantState,
                restaurantId: Number(restaurantId),
                restaurantName: restaurant?.restaurantName ?? restaurant?.Restaurant_Name ?? '',
            },
        };
    }
    const catalogParams = {
        ...getCatalogOptionsForTemplateSource(normalized),
    };
    if (catalogParams.salesChannel == null && salesChannel) {
        catalogParams.salesChannel = salesChannel;
    }
    const grouped = await fetchRestaurantFoodItems(restaurantId, catalogParams);
    return {
        items: grouped,
        meta: null,
    };
}

export function normalizeSalesChannel(val) {
    const channel = String(val ?? '').trim().toLowerCase().replace(/_/g, '-');
    if (channel === 'dinein' || channel === 'dine-in' || channel === 'dine') return 'dinein';
    if (channel === 'online' || channel === 'myeats') return 'online';
    return '';
}

/** GET /restaurants/:restaurantId/food-items */
export async function fetchRestaurantFoodItems(restaurantId, options = {}) {
    const token = localStorage.getItem('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const menuSource = options?.menuSource ?? options?.menu_source;
    const salesChannel = normalizeSalesChannel(
        options?.salesChannel ?? options?.sales_channel
    );
    const params = {};
    if (menuSource != null && String(menuSource).trim() !== '') {
        params.menuSource = normalizeMenuListSource(menuSource);
    }
    if (salesChannel) {
        params.salesChannel = salesChannel;
    }
    const response = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantFoodItemsByRestaurantId(restaurantId),
        { headers, params }
    );
    return mapFoodItemsApiResponse(response.data || {});
}

/** GET /restaurant/food-items/:foodItemId */
export async function fetchFoodItemById(foodItemId) {
    const id = Number(foodItemId);
    if (!id || Number.isNaN(id)) {
        throw new Error('foodItemId is required');
    }
    const token = localStorage.getItem('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.restaurantFoodItemById(id),
        { headers }
    );
    const normalized = normalizeOnlineFoodItem(response.data);
    if (!normalized) {
        throw new Error('Invalid food item response');
    }
    return normalized;
}

function restaurantAddressState(restaurant) {
    const addresses = restaurant?.addresses ?? [];
    const primary = Array.isArray(addresses) ? addresses[0] : null;
    return primary?.state != null ? String(primary.state).trim() : '';
}

/**
 * GET /customer/jreats/full-menu?state=…
 * Reads restaurant address state from company API, then loads Junior Eats catalog from customer API.
 */
export async function fetchJreatsFullMenuForRestaurant(restaurantId) {
    const restaurant = await fetchRestaurantById(restaurantId);
    const restaurantState = restaurantAddressState(restaurant);
    if (!restaurantState) {
        const err = new Error('Restaurant address state is required to load Junior Eats menu.');
        err.response = { data: { error: err.message, message: err.message }, status: 400 };
        throw err;
    }

    const token = localStorage.getItem('Token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axiosCustomerInstance.get(CUSTOMER_API_PATHS.customerJreatsFullMenu, {
        params: { state: restaurantState },
        headers,
    });
    const data = response.data || {};
    return {
        meta: {
            restaurantState,
            state: data.state ?? restaurantState,
            restaurantId: data.restaurantId,
            restaurantName: data.restaurantName,
            foodItemCount: data.foodItemCount,
            kitchens: data.restaurants,
        },
        items: mapFoodItemsApiResponse(data),
    };
}

/** Human-readable ingredients for table (array from API or legacy string). */
export function formatOnlineIngredientsDisplay(item) {
    if (!item) return '';
    if (Array.isArray(item.ingredients) && item.ingredients.length > 0) {
        return item.ingredients
            .map((ing) => ing.ingredientName || ing.ingredient_name || ing.name || '')
            .filter(Boolean)
            .join(', ');
    }
    const low = item.ingredients;
    if (typeof low === 'string' && low.trim()) return low.trim();
    const legacy = item.Ingredients;
    if (typeof legacy === 'string' && legacy.trim()) return legacy.trim();
    return '';
}

/** Parse ingredient names from display text (comma/semicolon list, else whitespace words). */
export function parseIngredientsDisplayList(fullDisplay = '') {
    const text = String(fullDisplay).trim();
    if (!text) return [];
    const byDelimiter = text
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(Boolean);
    if (byDelimiter.length > 1) return byDelimiter;
    return text.split(/\s+/).filter(Boolean);
}

/** Ingredient names for a menu row (API array or parsed display string). */
export function ingredientsListFromItem(item) {
    if (!item) return [];
    if (Array.isArray(item.ingredients) && item.ingredients.length > 0) {
        return item.ingredients
            .map((ing) => ing.ingredientName || ing.ingredient_name || ing.name || '')
            .filter(Boolean);
    }
    return parseIngredientsDisplayList(formatOnlineIngredientsDisplay(item));
}

/** Short table preview: first two words; full list stays in the popup. */
export function formatIngredientsTablePreview(ingredientsList, fullDisplay = '') {
    const list = Array.isArray(ingredientsList) ? ingredientsList.filter(Boolean) : [];
    const fullText = list.length > 0 ? list.join(', ') : String(fullDisplay).trim();
    if (!fullText) return '';
    const words = fullText.split(/\s+/).filter(Boolean);
    if (words.length <= 2) return words.join(' ');
    return `${words.slice(0, 2).join(' ')}…`;
}

/**
 * Title-case food item names for display (e.g. "dal rice" → "Dal Rice") while
 * preserving already-capitalized tokens like acronyms ("BCC Juice" stays "BCC Juice").
 */
export function formatFoodItemDisplayName(value) {
    if (value == null || String(value).trim() === '') return '';
    return String(value).replace(/\b\p{L}/gu, (ch) => ch.toUpperCase());
}
